import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import download from 'src/utils/download';
import { getRequest } from 'src/utils/request';
import {
  ErrorType,
  useMessageHandler,
} from 'src/utils/hooks/useMessageHandler';

import messages from '../../messages';

const downloadPayroll = async (payroll?: UCM.PayrollDataType) => {
  if (!payroll) return;
  const { data } = await getRequest(
    `${API.GET.downloadPayroll(payroll.uuid)}`,
    {
      responseType: 'blob',
    },
  );
  const fileName = payroll.objectKey?.substring(
    payroll.objectKey.lastIndexOf('/') + 1,
  );
  download(data, fileName, 'application/octet-stream');
  return data;
};

export const useDownloadPayroll = ({
  payroll,
  enabled = false,
}: {
  payroll?: UCM.PayrollDataType;
  enabled?: boolean;
}) => {
  const { handleError, handleSuccess } = useMessageHandler();
  const { formatMessage } = useIntl();
  const client = useQueryClient();

  const { isFetching, isSuccess, isError, error } = useQuery<
    unknown,
    ErrorType
  >({
    queryKey: [QUERY_KEYS.PAYROLL_DOWNLOAD],
    queryFn: () => downloadPayroll(payroll),
    enabled,
  });

  useEffect(() => {
    if (isSuccess) {
      handleSuccess(formatMessage(messages.payrollDownloaded));
      client.removeQueries({
        queryKey: [QUERY_KEYS.PAYROLL_DOWNLOAD],
      });
    }
    if (isError && error) {
      handleError(error);
    }
  }, [isSuccess, isError]);

  return { isSuccess, isFetching };
};
